// src/components/layout/Sidebar.styles.ts
import styled, { css } from 'styled-components';
import { NavLink } from 'react-router-dom';

export const LayoutContainer = styled.div<{ isSidebarOpen: boolean }>`
  .main-content {
    padding-left: ${({ isSidebarOpen }) => (isSidebarOpen ? '260px' : '88px')};
    transition: padding-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    @media (max-width: 768px) {
      padding-left: ${({ isSidebarOpen }) => (isSidebarOpen ? '200px' : '0px')};
    }

    @media (max-width: 480px) {
      padding-left: 0px;
    }
  }
`;

export const ContentContainer = styled.main`
  width: 100%;
  background-color: #f8f9fa;
  min-height: 100vh;
  padding: 0;

  @media (max-width: 768px) {
    padding: 0px;
  }

  @media (max-width: 480px) {
    padding: 0px;
  }
`;

const sharedItemStyles = css`
  position: relative;
  display: flex;
  align-items: center;
  height: 52px;
  padding: 0 24px;
  margin: 4px 12px;
  border-radius: 8px;
  color: #aeb9c5;
  text-decoration: none;
  transition: all 0.25s ease-in-out;
  white-space: nowrap;
  cursor: pointer;

  .icon {
    flex-shrink: 0;
    font-size: 20px;
    width: 20px;
    height: 20px;
    transition: all 0.25s ease-in-out;

    svg {
      width: 100%;
      height: 100%;
    }
  }

  .item-text {
    margin-left: 18px;
    font-size: 0.95rem;
    font-weight: 500;
    transition: opacity 0.2s ease-in-out;
  }

  .arrow-icon {
    margin-left: auto;
    transition: transform 0.25s ease;
  }

  &:hover {
    background-color: #003566;
    color: #ffffff;

    .icon {
      transform: scale(1.1);
    }
  }
`;

const activeIndicator = css`
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  height: 60%;
  width: 4px;
  background-color: #ff9e00;
  border-radius: 0 4px 4px 0;
`;

export const SidebarContainer = styled.aside<{ isOpen: boolean }>`
  width: ${({ isOpen }) => (isOpen ? '260px' : '88px')};
  background: #003566;
  display: flex;
  flex-direction: column;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-right: 1px solid #003566;
  z-index: 20;

  ${({ isOpen }) =>
    !isOpen &&
    css`
      .item-text,
      .arrow-icon {
        opacity: 0;
        pointer-events: none;
      }
      .icon {
        margin: auto;
      }
      .has-children:hover .flyout-submenu {
      
        visibility: visible;
        opacity: 1;
        transform: translateX(0);
        pointer-events: auto;
      }
    `}

  @media (max-width: 768px) {
    width: ${({ isOpen }) => (isOpen ? '220px' : '0px')};
    overflow-x: hidden;
    transition: width 0.3s ease;
  }

  @media (max-width: 480px) {
    position: fixed;
    height: 100%;
    width: ${({ isOpen }) => (isOpen ? '100%' : '0')};
    background: rgba(0, 53, 102, 0.95);
  }
`;

export const LogoContainer = styled.div<{ isOpen: boolean }>`
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
`;

const logoTextBase = css`
  position: absolute;
  font-size: 1.4rem;
  font-weight: 600;
  color: #ffffff;
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
`;

export const LogoText = styled.span<{ isOpen: boolean }>`
  ${logoTextBase}
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(-10px)')};
`;

export const LogoInitials = styled.span<{ isOpen: boolean }>`
  ${logoTextBase}
  opacity: ${({ isOpen }) => (isOpen ? 0 : 1)};
  transform: ${({ isOpen }) => (isOpen ? 'translateY(10px)' : 'translateY(0)')};
`;

export const NavList = styled.nav`
  flex-grow: 1;
  padding-top: 16px;

  @media (max-width: 480px) {
    padding: 8px;
  }
`;

export const NavItemContainer = styled.div`
  position: relative;
`;

export const NavItemLink = styled(NavLink)`
  ${sharedItemStyles}

  &.active {
    color: #ffffff;
    background-color: #007bff;

    &::before {
      ${activeIndicator}
    }
  }
`;

export const MenuItemButton = styled.div<{ isActive: boolean }>`
  ${sharedItemStyles}

  ${({ isActive }) =>
    isActive &&
    css`
      color: #ffffff;
      background-color: #007bff;

      &::before {
        ${activeIndicator}
      }
    `}
`;

export const SubMenu = styled.div<{ isOpen: boolean }>`
  overflow: hidden;
  max-height: ${({ isOpen }) => (isOpen ? '500px' : '0')};
  transition: max-height 0.3s ease-in-out;
  background-color: rgba(0, 0, 0, 0.2);
  padding-left: 32px;

  @media (max-width: 768px) {
    padding-left: 16px;
  }
`;

export const SubMenuItem = styled(NavLink)`
  ${sharedItemStyles}
  height: 44px;

  &.active {
    color: #ffffff;

    &::before {
      ${activeIndicator}
    }
  }
`;

export const FlyoutSubMenu = styled.div`
  position: absolute;
  left: 80px;
  top: 0;
  padding: 8px;
  border-radius: 12px;
  background-color: #ffffff;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 4px;

  visibility: hidden;
  opacity: 0;
  transform: translateX(-10px);
  transition: all 0.25s ease;
  z-index: 100;
  pointer-events: none;

  &.flyout-submenu {
    /* class hook for collapsed-sidebar hover/active rules */
  }

  @media (max-width: 768px) {
    position: static;
    transform: none;
    opacity: 1;
    visibility: visible;
    box-shadow: none;
    background: none;
    padding: 4px 0;
  }
`;

export const FlyoutSubMenuItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 8px;
  text-decoration: none;
  background: none;
  color: #333;
  margin: 0;
  white-space: nowrap;

  .icon {
    font-size: 18px;
    flex-shrink: 0;
    color: #003566;
  }

  .item-text {
    color: #003566;
    opacity: 1 !important;
    pointer-events: auto !important;
    font-weight: 500;
    font-size: 0.95rem;
  }

  &:hover {
    background-color: #f1f5f9;
  }

  &.active {
    background-color: #e9ecef;
    font-weight: bold;
  }
`;
